import React, { useMemo } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { Select, Button, Tag } from 'antd';
import { DataTable } from '@components/data-table';
import { TASK_INSTANCE_STATE_FILTERS, TASK_INSTANCES_LIMIT } from '../../constants';
import { AirflowTaskInstance } from '../../types';
import { formatDateTime, formatDurationSeconds, TaskInstanceStateBadge } from '../../utils';
import { useTaskInstancesTab } from './use-task-instances-tab';
import styles from '../../styles.module.scss';

const { Option } = Select;

type TaskInstancesTabProps = {
    dagId: string;
    taskId: string;
    refreshKey: number;
};

/*
 * DAG-task detail's Task Instances tab — every instance of this task across all runs
 */
export function TaskInstancesTab({ dagId, taskId, refreshKey }: TaskInstancesTabProps) {
    const { instances, totalEntries, isLoading, stateFilter, offset, handleStateFilterChange, handlePrevPage, handleNextPage } = useTaskInstancesTab(dagId, taskId, refreshKey);

    const columns = useMemo<ColumnDef<AirflowTaskInstance>[]>(
        () => [
            { id: 'dag_run', header: 'Dag Run', enableSorting: false, cell: ({ row }) => formatDateTime(row.original.execution_date ?? row.original.run_after ?? row.original.logical_date) || row.original.dag_run_id },
            { id: 'map_index', header: 'Map Index', enableSorting: false, cell: ({ row }) => ((row.original.map_index ?? -1) >= 0 ? row.original.map_index : '—') },
            { id: 'state', header: 'State', enableSorting: false, cell: ({ row }) => <TaskInstanceStateBadge state={row.original.state} /> },
            { id: 'start_date', header: 'Start Date', enableSorting: false, cell: ({ row }) => formatDateTime(row.original.start_date) },
            { id: 'try_number', header: 'Try Number', enableSorting: false, cell: ({ row }) => row.original.try_number ?? 1 },
            { id: 'operator', header: 'Operator', enableSorting: false, cell: ({ row }) => <Tag>{row.original.operator_name ?? row.original.operator ?? '—'}</Tag> },
            { id: 'duration', header: 'Duration', enableSorting: false, cell: ({ row }) => formatDurationSeconds(row.original.duration) },
        ],
        []
    );

    return (
        <div className={styles.card}>
            <div className={styles.filterRow}>
                <h3 style={{ margin: 0, color: '#fff', fontSize: 14, fontWeight: 700, letterSpacing: 0.25 }}>{`Task Instances (${totalEntries})`}</h3>
                <div className={styles.filterRowEnd}>
                    <Select value={stateFilter || undefined} placeholder="State" allowClear style={{ width: 160 }} onChange={(value) => handleStateFilterChange(value ?? '')}>
                        {TASK_INSTANCE_STATE_FILTERS.map((option) => (
                            <Option key={option.value} value={option.value}>
                                {option.label}
                            </Option>
                        ))}
                    </Select>
                </div>
            </div>
            <DataTable data={instances} columns={columns} isLoading={isLoading} rowKey={(t) => `${t.dag_run_id}-${t.map_index ?? -1}`} emptyText="No task instances found" />
            {totalEntries > TASK_INSTANCES_LIMIT && (
                <div className={styles.paginationRow} style={{ justifyContent: 'flex-end' }}>
                    <Button size="small" disabled={offset === 0} onClick={handlePrevPage} style={{ marginRight: 8 }}>
                        Prev
                    </Button>
                    <Button size="small" disabled={offset + TASK_INSTANCES_LIMIT >= totalEntries} onClick={handleNextPage}>
                        Next
                    </Button>
                </div>
            )}
        </div>
    );
}
